// moms-harness:anchor v2
// Spec: docs/spec/auth/LOGISPRD-10053-okta-app-access-denied.yaml

import { useCallback, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PrimaryButton, SecondaryButton } from '@musinsa/layer-components-desktop'
import AuthStatusPage from '@/pages/auth/components/AuthStatusPage'
import { callToRequestActivation } from '@/services/auth/apis'
import { AuthUtils } from '@/utils/AuthUtils'
import MAlert from '@/utils/modal/MAlert'
import { Utils } from '@/utils/Utils'

// Figma 33:14562 (비활성화된 계정) / 33:15182 (계정 활성화 승인대기중)
// 90일 미접속 자동 비활성화(LOGISPRD-9191) 후 진입하는 안내 화면.
// - DEACTIVATED : 비활성화 안내 + '계정 활성화 요청' 버튼
// - PENDING     : 활성화 요청 후 관리자 승인 대기 안내
type ActivationStatus = 'DEACTIVATED' | 'PENDING'

function Deactivated() {
  const [searchParams] = useSearchParams()

  // 임시: 계정 활성화 상태는 API 응답으로 내려오는 값을 전제한다.
  // 연동 전까지는 쿼리 파라미터(?pending=true)로 초기 상태를 결정한다.
  // TODO: 로그인/계정상태 조회 API 응답으로 초기값 대체.
  const initialStatus: ActivationStatus = searchParams.get('pending') === 'true' ? 'PENDING' : 'DEACTIVATED'
  const [status, setStatus] = useState<ActivationStatus>(initialStatus)

  // LOGISPRD-9448: '로그인으로 돌아가기' = 앱 표준 로그아웃과 동일하게 처리(AuthUtils.logout).
  // Okta 세션이면 oktaLogout()(전역 signOut: org 세션 종료 → postLogoutRedirectUri) — 헤더 로그아웃 등과 일관.
  // 콜백에서 deactivated 진입 시 Okta 세션을 보존(request-activation Bearer 용)하므로, 여기서 표준 로그아웃으로 정리.
  // → org 세션이 끝나 재로그인 시 같은 계정 자동 SSO 안 되고 fresh 로그인(계정 전환 가능). AuthUtils.logout 이 sessionStorage.clear 포함.
  const handleBackToLogin = useCallback(() => {
    AuthUtils.logout()
  }, [])

  // LOGISPRD-9448: 계정 활성화 요청 — POST /api/auth/request-activation (HANDOFF §10.3).
  // 성공(200, 멱등) → PENDING 전환. 실패 → 안내 alert 후 현재 화면 유지(상태 미변경).
  const handleRequestActivation = useCallback(async () => {
    try {
      await callToRequestActivation()
      setStatus('PENDING')
    } catch (e) {
      MAlert.show(Utils.getApiErrMessage(e, '활성화 요청에 실패했습니다. 잠시 후 다시 시도해주세요.'))
    }
  }, [])

  const isPending = status === 'PENDING'

  return (
    <AuthStatusPage
      iconName={isPending ? 'ic-clockCircle' : 'ic-lock'}
      title={isPending ? '계정 활성화\n승인대기중이에요' : '비활성화된 계정이에요'}
      description={
        isPending
          ? '관리자가 순차 확인 중이에요\n빠른 승인은 관리자에게 직접 요청하세요'
          : '90일 미사용으로 계정이 비활성화되었어요\n다시 이용하려면 활성화를 요청하세요'
      }
    >
      {!isPending && (
        <PrimaryButton variant="filled" size="xlarge" onClick={handleRequestActivation}>
          계정 활성화 요청
        </PrimaryButton>
      )}
      <SecondaryButton variant="ghost" size="xlarge" onClick={handleBackToLogin}>
        로그인으로 돌아가기
      </SecondaryButton>
    </AuthStatusPage>
  )
}

export default Deactivated
